import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardLayoutId, TierCheckpoint } from '../types';
import { playMissChime } from '../lib/audio/missChime';
import { playSuccessChime, primeSuccessChime } from '../lib/audio/successChime';
import { checkpointLabel } from '../lib/curriculum';
import { SPRINT_DURATION_MS, shuffleWords } from '../lib/wordSprint';
import Keyboard from './Keyboard';

interface WordSprintScreenProps {
  checkpoint: TierCheckpoint;
  keyboardLayout: KeyboardLayoutId;
  audioEnabled?: boolean;
  onFinish: (wordsCompleted: number, wordsSkipped: number, startedAt: string) => void;
  onCancel: () => void;
}

export default function WordSprintScreen({
  checkpoint,
  keyboardLayout,
  audioEnabled = true,
  onFinish,
  onCancel,
}: WordSprintScreenProps) {
  const words = useMemo(
    () => shuffleWords(checkpoint.sprintWords),
    [checkpoint.id, checkpoint.sprintWords],
  );
  const durationMs = checkpoint.sprintDurationMs ?? SPRINT_DURATION_MS;

  const [phase, setPhase] = useState<'ready' | 'playing'>('ready');
  const [wordIdx, setWordIdx] = useState(0);
  const [cursor, setCursor] = useState(0);
  const [wordsCompleted, setWordsCompleted] = useState(0);
  const [wordsSkipped, setWordsSkipped] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [lastPress, setLastPress] = useState<
    { key: string; correct: boolean } | undefined
  >();
  const [awaitingSpace, setAwaitingSpace] = useState(false);

  const [clockStarted, setClockStarted] = useState(false);
  const clockStartedRef = useRef(false);
  const startedAtRef = useRef<string>('');
  const endsAtRef = useRef(0);
  const armedAtRef = useRef(0);
  const finishedRef = useRef(false);
  const completedRef = useRef(0);
  const skippedRef = useRef(0);

  const startClock = useCallback(() => {
    if (clockStartedRef.current) return;
    clockStartedRef.current = true;
    const t = Date.now();
    startedAtRef.current = new Date(t).toISOString();
    endsAtRef.current = t + durationMs;
    setNow(t);
    setClockStarted(true);
  }, [durationMs]);

  const currentWord = words[wordIdx] ?? '';

  const finish = useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    if (!startedAtRef.current) {
      startedAtRef.current = new Date(armedAtRef.current).toISOString();
    }
    onFinish(
      completedRef.current,
      skippedRef.current,
      startedAtRef.current,
    );
  }, [onFinish]);

  const advanceWord = useCallback(() => {
    setAwaitingSpace(false);
    setWordIdx((i) => (i + 1) % words.length);
    setCursor(0);
    setLastPress(undefined);
  }, [words.length]);

  const skipWord = useCallback(() => {
    if (audioEnabled) playMissChime();
    skippedRef.current += 1;
    setWordsSkipped(skippedRef.current);
    advanceWord();
  }, [advanceWord, audioEnabled]);

  const completeWord = useCallback(() => {
    completedRef.current += 1;
    setWordsCompleted(completedRef.current);
    advanceWord();
  }, [advanceWord]);

  useEffect(() => {
    if (phase !== 'playing') return;
    const id = window.setInterval(() => setNow(Date.now()), 100);
    return () => window.clearInterval(id);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'playing' || !clockStarted) return;
    if (now >= endsAtRef.current) finish();
  }, [phase, clockStarted, now, finish]);

  useEffect(() => {
    if (phase !== 'playing') return;

    const handler = (e: KeyboardEvent) => {
      if (finishedRef.current) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        finish();
        return;
      }

      if (e.key === 'Backspace') {
        e.preventDefault();
        return;
      }

      if (e.key === ' ') {
        e.preventDefault();
        startClock();
        if (awaitingSpace) {
          setLastPress({ key: ' ', correct: true });
          completeWord();
        }
        return;
      }

      if (e.key.length !== 1) return;
      e.preventDefault();

      startClock();

      const word = words[wordIdx];
      if (!word) return;

      if (awaitingSpace) return;

      const expected = word[cursor];
      if (expected === undefined) return;

      const same = e.key.toLowerCase() === expected.toLowerCase();
      if (same) {
        setLastPress({ key: e.key, correct: true });
        const next = cursor + 1;
        if (next >= word.length) {
          if (audioEnabled) playSuccessChime();
          setAwaitingSpace(true);
        } else setCursor(next);
      } else {
        setLastPress({ key: e.key, correct: false });
        skipWord();
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [
    phase,
    words,
    wordIdx,
    cursor,
    awaitingSpace,
    audioEnabled,
    finish,
    skipWord,
    completeWord,
    startClock,
  ]);

  const remainingMs =
    phase === 'playing'
      ? clockStarted
        ? Math.max(0, endsAtRef.current - now)
        : durationMs
      : durationMs;

  const expectedChar = awaitingSpace ? ' ' : currentWord[cursor];

  if (words.length === 0) {
    return (
      <SprintShell>
        <p className="text-faint">Ingen ord defineret for denne test.</p>
        <button type="button" onClick={onCancel} className="btn-secondary mt-4">
          Tilbage
        </button>
      </SprintShell>
    );
  }

  return (
    <SprintShell>
      <CheckpointHeader checkpoint={checkpoint} onCancel={onCancel} />

      {phase === 'ready' ? (
        <div className="surface mx-auto w-full max-w-xl border border-amber-200/80 p-6 text-center animate-fade-in-up dark:border-amber-500/20">
          <p className="label text-amber-700 dark:text-amber-300">Pop-quiz</p>
          <h2 className="mt-2 text-2xl font-semibold text-strong">
            1 minut — så mange ord som muligt
          </h2>
          <p className="mt-2 text-muted">
            Skriv ordet tegn for tegn, tryk mellemrum mellem ordene — som i
            diktat. Uret starter ved første tast. Én fejl springer ordet over.
          </p>
          <button
            type="button"
            autoFocus
            onClick={() => {
              if (audioEnabled) primeSuccessChime();
              armedAtRef.current = Date.now();
              startedAtRef.current = '';
              finishedRef.current = false;
              completedRef.current = 0;
              skippedRef.current = 0;
              setWordsCompleted(0);
              setWordsSkipped(0);
              setWordIdx(0);
              setCursor(0);
              setAwaitingSpace(false);
              clockStartedRef.current = false;
              setClockStarted(false);
              setPhase('playing');
            }}
            className="mt-6 rounded-xl bg-amber-500 px-6 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-amber-400 dark:shadow-lg dark:shadow-amber-500/30"
          >
            Start test
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3">
            <Stat
              label="Tid"
              value={formatCountdown(remainingMs)}
              sub={!clockStarted ? 'Starter ved første tast' : undefined}
            />
            <Stat label="Ord" value={String(wordsCompleted)} />
            <Stat label="Sprunget over" value={String(wordsSkipped)} />
          </div>

          <div className="surface p-8 text-center">
            <p className="label mb-3">Skriv dette ord</p>
            <p className="font-mono text-4xl font-semibold tracking-wide text-strong">
              {currentWord ? (
                [...currentWord].map((ch, i) => {
                  const done = awaitingSpace || i < cursor;
                  const active = !awaitingSpace && i === cursor;
                  let cls = 'text-zinc-400 dark:text-zinc-500';
                  if (done) cls = 'text-emerald-600 dark:text-emerald-300';
                  else if (active)
                    cls =
                      'text-zinc-900 dark:text-zinc-100 bg-amber-100 dark:bg-amber-500/10 rounded-sm border-b-2 border-amber-500';
                  return (
                    <span key={i} className={cls}>
                      {ch}
                    </span>
                  );
                })
              ) : (
                <span className="text-muted">—</span>
              )}
            </p>
            {awaitingSpace && (
              <p className="mt-4 text-sm font-medium text-emerald-600 dark:text-emerald-300 animate-fade-in-up">
                Tryk mellemrum for næste ord
              </p>
            )}
          </div>

          <Keyboard
            layoutId={keyboardLayout}
            nextExpectedKey={expectedChar}
            lastPressedKey={lastPress?.key}
            lastPressCorrect={lastPress?.correct}
            unlockedKeys={checkpoint.unlockedKeys}
            hintLevel="none"
            showFingerDot={false}
          />

          <p className="text-center text-xs text-faint">
            Mellemrum mellem ord. Forkert tast springer ordet over. Escape
            afslutter testen.
          </p>
        </>
      )}
    </SprintShell>
  );
}

function CheckpointHeader({
  checkpoint,
  onCancel,
}: {
  checkpoint: TierCheckpoint;
  onCancel: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div>
        <p className="label text-amber-700 dark:text-amber-300">
          {checkpointLabel(checkpoint)} · Niveau-test · 60 sek
        </p>
        <h1 className="mt-1 text-2xl font-semibold text-strong">{checkpoint.title}</h1>
        <p className="text-sm text-muted">{checkpoint.goal}</p>
      </div>
      <button type="button" onClick={onCancel} className="btn-secondary shrink-0">
        Afslut
      </button>
    </div>
  );
}

function SprintShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6">{children}</div>
  );
}

function Stat({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="surface-soft px-4 py-3">
      <p className="label">{label}</p>
      <p className="mt-1 font-mono text-2xl text-strong">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-faint">{sub}</p>}
    </div>
  );
}

function formatCountdown(ms: number): string {
  const total = Math.ceil(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
